import type { Person } from '@/store/people/types';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  Button,
  DatePicker,
} from '@heroui/react';
import { useState, useCallback } from 'react';
import {
  useCreateMilestone,
  useUpdateMilestone,
} from '@/store/milestones/hooks';
import type {
  Milestone,
  MilestoneCreate,
  MilestoneUpdate,
} from '@/store/milestones/types';
import { useUserId } from '@/hooks/auth';
import { CalendarDate } from '@internationalized/date';
import { Name } from '@/components/common/output/name';
import { getFullName } from '@/shared/lib/names';
import { useModalSize } from '@/hooks/screen';
import { MilestoneSelect } from '../common/input/milestone';
import { MilestoneItem } from '../common/output/milestone';

export function MilestoneModal({
  isOpen,
  onOpenChange,
  target,
  milestone,
  access = { canEdit: true },
  onCreate,
  onUpdate,
}: {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  target: Person | null;
  milestone?: Milestone | null;
  access?: {
    canEdit: boolean;
  };
  onCreate?: (item: Milestone) => void;
  onUpdate?: (item: Milestone) => void;
}) {
  const modalSize = useModalSize();
  const isEdit = Boolean(milestone);
  if (!isOpen) {
    return null;
  }

  if (!isEdit && !target) {
    return null;
  }

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      scrollBehavior='inside'
      placement='center'
      size={modalSize}
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader>
              {isEdit ? 'Edit Milestone' : 'Add Milestone'}
            </ModalHeader>
            <ModalBody>
              <MilestoneForm
                target={target}
                milestone={milestone}
                onClose={onClose}
                access={access}
                isEdit={isEdit}
                onCreate={onCreate}
                onUpdate={onUpdate}
              />
            </ModalBody>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}

function MilestoneForm({
  target,
  milestone,
  onClose,
  isEdit = false,
  onCreate,
  onUpdate,
}: {
  target: Person | null;
  milestone?: Milestone | null;
  onClose?: () => void;
  access: {
    canEdit: boolean;
  };
  isEdit?: boolean;
  onCreate?: (item: Milestone) => void;
  onUpdate?: (item: Milestone) => void;
}) {
  const userId = useUserId();
  const subject = getFullName(target?.data || {});

  const [milestoneId, setMilestoneId] = useState(
    milestone?.data.milestone_id || '',
  );
  const updateMilestoneId = (value: string | undefined) => {
    setMilestoneId(value || '');
  };

  const [date, setDate] = useState<CalendarDate | null>(
    milestone?.data.occurred_at
      ? new CalendarDate(
          new Date(milestone.data.occurred_at).getFullYear(),
          new Date(milestone.data.occurred_at).getMonth() + 1,
          new Date(milestone.data.occurred_at).getDate(),
        )
      : null,
  );

  const { createMilestone, creating } = useCreateMilestone();
  const { updateMilestone, updating } = useUpdateMilestone();

  const isPosting = creating;
  const isUpdating = updating;

  const handleCreateMilestone = useCallback(
    async (milestoneData: MilestoneCreate) => {
      try {
        console.log('createMilestone', milestoneData);
        const item = await createMilestone(milestoneData);
        if (onCreate) {
          debugger;
          onCreate(item);
        }

        return true;
      } catch (error) {
        console.error('Failed to create milestone:', error);

        return false;
      } finally {
        onClose?.();
      }
    },
    [createMilestone, onClose, onCreate],
  );

  const handleUpdateMilestone = useCallback(
    async (milestone: Milestone) => {
      try {
        console.log('updateMilestone', milestone);
        const res = await updateMilestone(milestone);
        if (onUpdate) {
          onUpdate(res);
        }

        return true;
      } catch (error) {
        console.error('Failed to update milestone:', error);

        return false;
      } finally {
        onClose?.();
      }
    },
    [onClose, updateMilestone, onUpdate],
  );

  const handleSave = useCallback(async () => {
    if (!milestoneId || !date || !createMilestone || !userId) {
      return;
    }

    const occurredDate = new Date(date.year, date.month - 1, date.day);
    const occurredISO = occurredDate.toISOString();

    if (isEdit && milestone) {
      const updateData: Partial<Milestone['data']> = {};

      if (milestoneId !== milestone.data.milestone_id) {
        updateData.milestone_id = milestoneId;
      }

      if (occurredISO !== milestone.data.occurred_at) {
        updateData.occurred_at = occurredISO;
      }

      if (Object.keys(updateData).length > 0) {
        const milestoneData: MilestoneUpdate = {
          ...milestone.data,
          ...updateData,
        };
        console.log('milestoneData UPDATING', milestoneData);
        await handleUpdateMilestone({
          id: milestone.id,
          data: milestoneData as Milestone['data'],
        });
      }
    } else {
      const milestoneData: MilestoneCreate = {
        id: '',
        data: {
          profile_id: target!.id,
          milestone_id: milestoneId,
          occurred_at: occurredISO,
          created_by: userId,
        },
      };

      const success = await handleCreateMilestone(milestoneData);
      if (success) {
        setMilestoneId('');
        setDate(null);
        onClose?.();
      }
    }
  }, [
    milestoneId,
    date,
    userId,
    target,
    milestone,
    isEdit,
    onClose,
    handleCreateMilestone,
    handleUpdateMilestone,
    createMilestone,
  ]);

  if (!userId || (!isEdit && !target)) {
    return null;
  }

  return (
    <div className='mb-2 flex flex-col gap-4'>
      <div className='flex flex-row'>
        <Name
          size='sm'
          value={subject}
          className='flex-shrink-0 rounded-full bg-default-200 p-1 pr-4'
        />
        <div className='flex-grow' />
      </div>
      {!isEdit ? (
        <MilestoneSelect
          label='Milestone'
          value={milestoneId}
          onChange={updateMilestoneId}
        />
      ) : (
        <MilestoneItem id={milestoneId} />
      )}
      <div className='flex flex-col gap-4 md:flex-row'>
        <DatePicker
          label='Date'
          value={date}
          onChange={setDate}
          className='flex-1'
        />
      </div>
      <div className='mt-4 flex flex-row justify-end'>
        <Button
          variant='solid'
          color='primary'
          onPress={handleSave}
          isDisabled={!milestoneId || !date || isUpdating || isPosting}
          isLoading={isUpdating || isPosting}
        >
          {isEdit ? 'Update' : 'Create'}
        </Button>
      </div>
    </div>
  );
}
